import { Button, Switch } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { useTheme } from '../../contexts/ThemeContext';
import { trackEvent } from '../../utils/analytics';
import { getConsentData, markBannerShown } from '../../utils/cookieConsentStorage';
import { isAdaptivePath } from '../Marketing/adaptivePaths';
import './CookieBanner.css';

/**
 * CookieBanner v2
 * - Shown globally (geo-gating removed in CookieConsentContext)
 * - Compact, rounded floating bar (single row on desktop)
 * - Real granular control: a "Customize" panel exposes per-category toggles
 *   (Essential is locked on, Analytics is user-controlled) and saves via the
 *   context's updateChoices(). Accept all / Reject all remain one-click.
 */
const CookieBanner = () => {
  const { showBanner, settingsOpen, acceptAll, rejectAll, updateChoices } = useCookieConsent();
  const { isDarkMode } = useTheme();
  // Logged-out visitors sit on the dark-locked MARKETING surface, whose primary
  // CTA convention is a WHITE PILL (matching the header Sign up + landing
  // --aie-cta-bg), not Ant's teal primary. The banner shows globally, so we tag
  // that surface to match it. Logged-in app keeps the teal Ant primary.
  const { currentUser } = useAuth();
  const isMarketing = !currentUser;
  // System-adaptive surfaces follow html[data-mkt-theme], set pre-paint by
  // the index.html boot script. The page list is the shared adaptivePaths
  // module (one list with MarketingLayout — batch 1, Aug 23 pm). On the
  // LIGHT face the banner drops both the dark skin and the marketing
  // white-pill override (Ant's light teal primary matches the blog-token
  // page). Dark-locked marketing pages keep the white pill.
  const { pathname } = useLocation();
  const adaptiveLight =
    isMarketing &&
    isAdaptivePath(pathname) &&
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-mkt-theme') === 'light';

  const [showDetails, setShowDetails] = useState(false);
  // Customize toggle default: reflect a previously saved choice — the banner
  // can be reopened via footer "Cookie preferences" AFTER a rejection, and it
  // must not present Analytics as ON for someone who turned it off. New
  // visitors with no record keep the optimistic ON default.
  const [analyticsEnabled, setAnalyticsEnabled] = useState(() => {
    const saved = getConsentData();
    return saved && saved.choices ? saved.choices.analytics === true : true;
  });

  /* Delayed reveal (Aug 6, Nitish; extended to 10s Aug 7): the banner appears after ~10s OR on the
     visitor's first scroll, whichever comes first — never instantly over the
     hero. Legally safe: GDPR/ePrivacy regulate what FIRES before consent, not
     when the banner shows, and Consent Mode already defaults EU users to
     denied until a choice is made (nothing non-essential runs pre-consent).
     Delayed banners also measurably improve consent rates vs instant pop-ups.
     The scroll listener is capture-phase on document so it works in BOTH
     shells (window scroll on marketing pages, container scroll in the app). */
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!showBanner || revealed) return undefined;
    // The build's prerender crawl (react-snap) must never reveal the card: it would bake the card and its class into the HTML.
    if (typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent)) return undefined;
    if (settingsOpen) {
      // Deliberate open via "Cookie preferences": show immediately. No
      // cooldown stamp and no cookie_banner_shown event — that denominator is
      // for unsolicited banners only.
      setRevealed(true);
      return undefined;
    }
    /* cookie_banner_shown (shift 89): the DENOMINATOR for consent-rate math.
       Existing cookie_consent_choice events only fire on a click, so without
       this we can't compute accept/reject/IGNORE rates or measure whether the
       banner redesign moved them. reveal_trigger tells us how the visitor met
       the banner (10s timer vs first scroll). Pre-consent this arrives via
       Consent Mode's cookieless ping, same caveat as the reject event below. */
    const reveal = (trigger) => {
      setRevealed(true);
      markBannerShown(); // start the 7-day ignore-cooldown (geo-split v3)
      trackEvent('cookie_banner_shown', { reveal_trigger: trigger });
    };
    const onTimer = () => reveal('timer');
    const onScroll = () => reveal('scroll');
    const timer = setTimeout(onTimer, 10000);
    document.addEventListener('scroll', onScroll, { capture: true, passive: true, once: true });
    return () => {
      clearTimeout(timer);
      document.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [showBanner, revealed, settingsOpen]);

  // The card sits bottom right where the support chat button sits: mark the page while it shows so the chat
  // steps aside until the visitor answers (Oct 28 2026, one ask at a time).
  useEffect(() => {
    if (!showBanner || !revealed) {
      // A prerendered page can carry this class from the build's own crawl (Nov 2 2026: home shipped with it baked into
      // <body>, so the chat hid itself on home and on every page reached from it). No card showing = no class.
      document.body.classList.remove('cc-cookie-open');
      return undefined;
    }
    document.body.classList.add('cc-cookie-open');
    return () => document.body.classList.remove('cc-cookie-open');
  }, [showBanner, revealed]);

  if (!showBanner || !revealed) {
    return null;
  }

  const saveChoices = () => updateChoices({ analytics: analyticsEnabled });

  return (
    <div
      className={`cookie-banner ${isDarkMode && !adaptiveLight ? 'dark' : 'light'}${isMarketing && !adaptiveLight ? ' is-marketing' : ''}`}
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
    >
      <div className="cookie-banner-content">
        <div className="cookie-banner-main">
          <div className="cookie-banner-text">
            <span className="cookie-banner-icon" aria-hidden="true">🍪</span>
            <p className="cookie-banner-message">
              We use essential cookies to keep you signed in and optional analytics
              cookies to improve CellCog.{' '}
              <Link to="/support/privacy-policy" className="cookie-policy-link">
                Privacy Policy
              </Link>
            </p>
          </div>

          <div className="cookie-banner-actions">
            {!showDetails && (
              <Button
                size="small"
                type="text"
                onClick={() => setShowDetails(true)}
                className="cookie-customize-btn"
              >
                Customize
              </Button>
            )}

            <Button
              size="small"
              onClick={rejectAll}
              className="cookie-reject-btn"
            >
              Reject all
            </Button>

            {showDetails ? (
              <Button
                size="small"
                type="primary"
                onClick={saveChoices}
                className="cookie-accept-btn"
              >
                Save choices
              </Button>
            ) : (
              <Button
                size="small"
                type="primary"
                onClick={acceptAll}
                className="cookie-accept-btn"
              >
                Accept all
              </Button>
            )}
          </div>
        </div>

        {showDetails && (
          <div className="cookie-banner-details">
            <div className="cookie-category">
              <div className="cookie-category-info">
                <span className="cookie-category-title">Essential</span>
                <span className="cookie-category-desc">
                  Required to sign in and keep the site secure. Always on.
                </span>
              </div>
              <Switch checked disabled size="small" aria-label="Essential cookies (always on)" />
            </div>

            <div className="cookie-category">
              <div className="cookie-category-info">
                <span className="cookie-category-title">Analytics</span>
                <span className="cookie-category-desc">
                  Helps us understand usage so we can improve the product.
                </span>
              </div>
              <Switch
                checked={analyticsEnabled}
                onChange={setAnalyticsEnabled}
                size="small"
                aria-label="Analytics cookies"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
